import { cn } from '@/lib/utils';
import { STATUS_LABEL, type StatusKey } from '@/lib/copy';

/**
 * The COLOURS for a drone state, then a mission state. The words are in `lib/copy/status.ts`,
 * and this component's `uppercase` class is what puts them in capitals on screen.
 *
 * Green is reserved for "airborne right now" — `flying` for a drone, `in_progress` for a
 * mission. A scheduled mission is blue (planned/standby), not green: reusing green for
 * "flying later" made a planned mission indistinguishable from one in the air at a glance.
 *
 * `covered_no_sortie` is the exception that proves the rule, and it is why this vocabulary
 * needed a new word rather than a new colour. Continuous coverage (#248) attaches a rotation
 * of aircraft to a station and deliberately does NOT complete the mission when one lands: it
 * stays `in_progress` with nothing in the air until an operator launches the next aircraft.
 * That is a normal steady state, not a fault — but it is not `in_progress`-as-green either,
 * because nothing is flying. Rendering it green made the COP claim an airborne aircraft over
 * an empty sky, which is exactly the <3-second misread the status palette exists to prevent.
 * It takes AMBER, from the existing palette (`--status-warning`), on the same reasoning
 * `cancelled` does: a station standing empty is a degraded outcome an operator should scan
 * for, and amber is already this product's "degraded, look here" register. No new colour
 * token was invented — the five status colours are the whole vocabulary.
 */
type Status = StatusKey;

interface StatusBadgeProps {
  status: Status;
  className?: string;
}

const statusConfig: Record<Status, { bgColor: string; textColor: string }> = {
  flying: { bgColor: 'bg-status-green/20', textColor: 'text-status-green-fg' },
  active: { bgColor: 'bg-status-green/20', textColor: 'text-status-green-fg' },
  online: { bgColor: 'bg-status-blue/20', textColor: 'text-status-blue-fg' },
  idle: { bgColor: 'bg-status-blue/20', textColor: 'text-status-blue-fg' },
  charging: { bgColor: 'bg-status-amber/20', textColor: 'text-status-amber-fg' },
  warning: { bgColor: 'bg-status-amber/20', textColor: 'text-status-amber-fg' },
  maintenance: { bgColor: 'bg-status-gray/20', textColor: 'text-status-gray-fg' },
  offline: { bgColor: 'bg-status-gray/20', textColor: 'text-status-gray-fg' },
  critical: { bgColor: 'bg-status-red/20', textColor: 'text-status-red-fg' },

  // Mission states. The label IS the mission status, so callers render the badge alone
  // instead of a badge plus a second status word that disagrees with it.
  draft: { bgColor: 'bg-status-gray/20', textColor: 'text-status-gray-fg' },
  scheduled: { bgColor: 'bg-status-blue/20', textColor: 'text-status-blue-fg' },
  in_progress: { bgColor: 'bg-status-green/20', textColor: 'text-status-green-fg' },
  // The label names the drone state, not the mission status, because the mission status is
  // the thing that misleads here — an operator reading "In progress" over an empty station has
  // been told the wrong fact accurately. "Covered" says the capability is still attached and
  // the mission is still running; "nothing flying" says exactly that. The words live in
  // `lib/copy/status.ts`, which also records why "NO SORTIE" was retired.
  covered_no_sortie: { bgColor: 'bg-status-amber/20', textColor: 'text-status-amber-fg' },
  // GRAY, AND IT IS THE ONE COLOUR CHOICE IN THIS FILE MADE BY ELIMINATION (#471).
  //
  // The server can now say it does not know whether anything is flying — a coverage lookup that
  // threw, on either transport. That is a fourth thing to draw, and the first three colours were
  // all wrong for it. GREEN asserts an airborne aircraft, which is the misread the state above
  // exists to prevent. AMBER is already `covered_no_sortie`, and that is a NORMAL steady state: a
  // manned station between sorties. Giving "the database did not answer" the same amber makes
  // routine and broken the same colour in the <3-second scan, which costs more than it buys. RED
  // is an operational emergency — an aircraft in trouble — and a failed read on one decoration is
  // not that.
  //
  // Gray is already this palette's "no claim" register: `offline`, `maintenance`, `draft`. It
  // says the platform is not asserting anything here, which is exactly true, and it cannot be
  // confused with any of the three states that ARE assertions.
  coverage_unknown: { bgColor: 'bg-status-gray/20', textColor: 'text-status-gray-fg' },
  completed: { bgColor: 'bg-status-gray/20', textColor: 'text-status-gray-fg' },
  // Amber, not gray: an aborted mission is a degraded outcome an ops lead scans the log for,
  // and it keeps "Canceled" distinguishable from "Completed" by more than the word alone.
  cancelled: { bgColor: 'bg-status-amber/20', textColor: 'text-status-amber-fg' },
};

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.offline;

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium tracking-wider uppercase',
        config.bgColor,
        config.textColor,
        className,
      )}
    >
      {STATUS_LABEL[status] ?? STATUS_LABEL.offline}
    </span>
  );
}
